import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import { PUBLIC_BOOKING } from "@/constants/testIds";

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function StepDateTime({ clinic, service, professional, onSelect, onBack }) {
  const { t, lang } = usePublicLang();
  const days = useMemo(() => Array.from({ length: 21 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  }), []);
  const [selectedDate, setSelectedDate] = useState(days[0]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setSelectedSlot(null);
    const params = { service_id: service.id, date: toDateStr(selectedDate) };
    if (professional?.id) params.professional_id = professional.id;
    api.get(`/public/clinics/${clinic.slug}/availability`, { params })
      .then(({ data }) => { if (active) setSlots(data.slots); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic.slug, service.id, professional?.id, selectedDate]);

  const locale = lang === "es" ? "es-ES" : "en-US";

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-[#5C554D] underline underline-offset-4 mb-6 inline-block"
        data-testid={`${PUBLIC_BOOKING.backButtonPrefix}3`}
      >
        &larr; {t("common.back")}
      </button>

      <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#5C554D] mb-3">{t("step3.dateLabel")}</p>
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" data-testid={PUBLIC_BOOKING.dateStrip}>
        {days.map((d) => {
          const dateStr = toDateStr(d);
          const active = dateStr === toDateStr(selectedDate);
          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => setSelectedDate(d)}
              className={`flex flex-col items-center justify-center shrink-0 w-16 h-20 rounded-2xl border transition-all ${active ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[#2D2926] border-[rgba(140,125,112,0.2)]"}`}
              data-testid={`${PUBLIC_BOOKING.dateOptionPrefix}${dateStr}`}
            >
              <span className="text-[10px] uppercase tracking-wide opacity-80">{d.toLocaleDateString(locale, { weekday: "short" })}</span>
              <span className="text-lg font-medium mt-1">{d.getDate()}</span>
              <span className="text-[10px] opacity-80">{d.toLocaleDateString(locale, { month: "short" })}</span>
            </button>
          );
        })}
      </div>

      <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#5C554D] mt-8 mb-3">{t("step3.timeLabel")}</p>
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-primary)]" />
        </div>
      ) : slots.length === 0 ? (
        <p className="text-sm text-[#5C554D] py-8 text-center" data-testid={PUBLIC_BOOKING.slotsEmpty}>{t("step3.noSlots")}</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid={PUBLIC_BOOKING.slotsGrid}>
          {slots.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSelectedSlot(s)}
              className={`h-12 rounded-full text-sm font-medium border transition-all ${selectedSlot === s ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[#2D2926] border-[rgba(140,125,112,0.2)] hover:border-[var(--brand-primary)]"}`}
              data-testid={`${PUBLIC_BOOKING.slotOptionPrefix}${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        disabled={!selectedSlot}
        onClick={() => onSelect({ date: toDateStr(selectedDate), time: selectedSlot })}
        className="w-full h-14 bg-[var(--brand-accent)] text-white rounded-md text-sm font-medium uppercase tracking-[0.16em] transition-all active:scale-[0.98] disabled:opacity-40 mt-8"
        data-testid={PUBLIC_BOOKING.dateTimeContinueButton}
      >
        {t("common.continue")}
      </button>
    </div>
  );
}
